import APIConfig from "../config/apiConfig";
import APIService from "service/network/APIService";
import Helper from "util/Helper";

/**
 * Call notes, v2 — keyed on (patientId, appointmentId).
 *
 * WHY THIS FILE GOT SMALLER. A v1 note hung off a CALL PLAN entry, call plans
 * are authored by hand on the ops Call Plan screen, and nothing server-side
 * linked one to an appointment. So this service used to fetch the patient's
 * call plan over a date window, anchor that window on local midnight of the
 * appointment's UTC calendar day, widen it to survive the server snapping the
 * bounds, then sort by nearness so the modal could offer a picker — an
 * elaborate apparatus for guessing a link that did not exist, whose failure
 * mode was a permanently disabled Send button.
 *
 * v2 is keyed on the two ids the screen already has. The window, the sort, the
 * midnight-anchoring and the picker are all gone, and none of them is missed.
 *
 * The v1 endpoints are untouched on the server and still serve bridge,
 * doctor-dashboard and expert-dashboard. phr no longer calls them.
 */
class CallNoteService {
  constructor() {
    this.apiService = new APIService();
    // Side effect: installs String.prototype.format for the URL templates.
    this.helper = new Helper();
  }

  /**
   * The notes already on this appointment, newest first.
   *
   * Answers a bare array — `[]` when there are none, never null — so a caller
   * need not guard both. This is what makes the once-only rule survive the
   * modal closing: `sent` is component state, so without a read there is
   * nothing to stop a second push of a note the patient already has.
   */
  listForAppointment(patientId, appointmentId, onResponse, onError) {
    if (!patientId || !appointmentId) {
      onResponse([]);
      return undefined;
    }
    const url = APIConfig.callNoteV2.byAppointment.format(patientId, appointmentId);

    return this.apiService.get(
      url,
      (res) => onResponse(Array.isArray(res) ? res : []),
      onError
    );
  }

  /**
   * Persist a note. `content` is HTML. Answers the created note, whose `id` is
   * what the chat push carries.
   *
   * `postAgain`, never `post`: post() re-fires on any non-2xx, and the server
   * has no uniqueness constraint, no pre-read and no upsert — just an
   * InsertOne — so a retry is a second note rather than a retry.
   *
   * It is also right for authorisation. api-calls-service rewrites every
   * upstream auth failure, 403 included, into a flat 401, and phr's get/put
   * answer a 401 by refreshing the token and, on failure, LOGGING THE USER OUT.
   * `postAgain` has no 401 branch, so an operator who simply lacks the
   * call-note permission sees an error here instead of being signed out.
   * Don't "fix" this to `post`.
   *
   * `doctorId` may legitimately be 0 — an ops employee has no doctorProfile —
   * and the server accepts it, omitting the doctor block on the read rather
   * than rejecting the write.
   */
  create({ patientId, appointmentId, doctorId, contentHtml }, onResponse, onError) {
    return this.apiService.postAgain(
      APIConfig.callNoteV2.new,
      {
        patientId: Number(patientId),
        appointmentId: Number(appointmentId),
        doctorId: Number(doctorId) || 0,
        content: contentHtml,
      },
      onResponse,
      onError
    );
  }

  /**
   * Push a SAVED note into the patient's chat — the body carries the note id as
   * a string, not the text, so the note must exist first.
   *
   * The id is a NUMBER for every note created from 2026-10-14 (1,000,000,001
   * and up) and a 24-char ObjectID for the v2 notes before it; it goes out as
   * text either way. The number is what the RELEASED patient app needs: it
   * opens every call note through the v1 details route and files the reply
   * under the reply's id, which api-calls-service echoes back for a number. No
   * number can echo an ObjectID, so those older notes stay blank in that build.
   */
  sendToChat(patientId, noteId, onResponse, onError) {
    const url = APIConfig.callNote.directMessage.format(patientId);

    return this.apiService.postAgain(
      url,
      { message: `${noteId}`, action: "call_note" },
      onResponse,
      onError
    );
  }

  /**
   * Mark a note as sent so it cannot be pushed twice. Fired the moment the chat
   * accepts it, and irreversible from the UI.
   */
  disable(noteId, onResponse, onError) {
    const url = APIConfig.callNoteV2.disable.format(noteId);

    return this.apiService.put(url, {}, onResponse, onError);
  }
}

export default CallNoteService;
